import React, { useState, useEffect, useContext } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Grid2,
} from "@mui/material";
import performRequest from "network/perform-request";
import UserContext from "context/UserContext";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import {
  getOrganizationMemberAdminRoute,
  getAssignableOfficialCategoriesRoute,
  createOrganizationMemberRoute,
  updateOrganizationMemberRoute,
} from "network/api-routes";
import OfficialCategoryAssignmentPanel from "./OfficialCategoryAssignmentPanel";
import MemberUserLinkPanel from "./MemberUserLinkPanel";
import { withStyles } from "@mui/styles";

const styles = (theme) => ({
  section: {
    backgroundColor: theme.palette.background.base,
  },
});

const Section = ({ title, description, children, classname }) => (
  <Paper variant="outlined" className={classname} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 4, height: "100%" }}>
    <Typography variant="h6">{title}</Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {description}
      </Typography>
    )}
    {!description && <Box mb={2} />}
    {children}
  </Paper>
);

function EditMemberPage({ memberId, organization, onClose, onSuccess, classes }) {
  const { user } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [assignableCategories, setAssignableCategories] = useState([]);
  const [initialCategoryId, setInitialCategoryId] = useState("");

  // Fetch datos del miembro
  useEffect(() => {
    if (memberId) {
      performRequest("GET", getOrganizationMemberAdminRoute(organization.id, memberId), {}, true, user)
        .then((res) => {
          setMember(res.data.data);
          setLoading(false);
        })
        .catch((err) => {
          enqueueSnackbar("Error al obtener los datos del miembro", { variant: "error" });
        })
        .finally(() => setLoading(false));
    } else {
      // Modo crear nuevo miembro
      setMember({
        name: "",
        cellphone_number: "",
        email: "",
        document_number: "",
        member_number: "",
        birth_date: "",
        is_active: true,
      });
      setLoading(false);
      setCategoriesLoading(true);
      performRequest("GET", getAssignableOfficialCategoriesRoute(organization.id), {}, true, user)
        .then((res) => setAssignableCategories(res.data.data || []))
        .catch((err) => {
          enqueueSnackbar(err?.response?.data?.message || "No se pudieron cargar las categorías oficiales", {
            variant: "error",
          });
        })
        .finally(() => setCategoriesLoading(false));
    }
  }, [memberId, organization, user, enqueueSnackbar]);

  const handleChange = (field) => (e) => {
    setMember({ ...member, [field]: e.target.value });
  };

  const handleSwitchChange = (e) => {
    setMember({ ...member, is_active: e.target.checked });
  };

  const handleUserLinkChanged = (linkData) => {
    setMember((currentMember) => ({ ...currentMember, user: linkData.user }));
    if (onSuccess) onSuccess();
  };

  const handleSubmit = () => {
    const method = memberId ? "PUT" : "POST";
    const url = memberId ? updateOrganizationMemberRoute(memberId) : createOrganizationMemberRoute;

    const payload = {
      name: member.name,
      cellphone_number: member.cellphone_number || null,
      email: member.email || null,
      document_number: member.document_number || null,
      member_number: member.member_number || null,
      birth_date: member.birth_date || null,
      is_active: member.is_active,
      organization_id: organization.id,
      ...(!memberId && initialCategoryId
        ? { official_category_assignment: { category_id: Number(initialCategoryId) } }
        : {}),
    };

    setSaving(true);
    performRequest(method, url, payload, true, user)
      .then(() => {
        enqueueSnackbar(memberId ? "Miembro actualizado con éxito" : "Miembro creado con éxito", {
          variant: "success",
        });

        if (onSuccess) onSuccess(); // para refrescar la tabla
        if (onClose) onClose(); // para cerrar el modal
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || "Error al guardar el miembro", { variant: "error" });
      })
      .finally(() => setSaving(false));
  };

  if (loading || !member) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  const linkedUser = member.user;
  const selectedCategory = assignableCategories.find((category) => String(category.id) === String(initialCategoryId));

  return (
    <Box sx={{ width: "min(940px, 94vw)", mx: "auto", my: { xs: 1, sm: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, maxHeight: "92vh", overflowY: "auto" }}>
        <Stack spacing={0.5} mb={3}>
          <Typography variant="h5">{memberId ? "Información del miembro" : "Crear nuevo miembro"}</Typography>
        </Stack>

        <Stack spacing={1}>
          <Section classname={classes.section} title="Información personal">
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  required
                  fullWidth
                  label="Nombre y apellido"
                  value={member.name}
                  onChange={handleChange("name")}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  label="Número de documento"
                  value={member.document_number || ""}
                  onChange={handleChange("document_number")}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  type="date"
                  label={intl.formatMessage({ id: "Fecha de nacimiento" })}
                  value={member.birth_date || ""}
                  onChange={handleChange("birth_date")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid2>
            </Grid2>
          </Section>

          <Section title="Contacto" classname={classes.section}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={member.cellphone_number || ""}
                  onChange={handleChange("cellphone_number")}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={member.email || ""}
                  onChange={handleChange("email")}
                />
              </Grid2>
            </Grid2>
          </Section>

          <Grid2 container spacing={1}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Section title="Membresía" classname={classes.section}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Número de miembro"
                    value={member.member_number || ""}
                    onChange={handleChange("member_number")}
                  />
                  <FormControlLabel
                    control={
                      <Switch checked={Boolean(member.is_active)} onChange={handleSwitchChange} color="primary" />
                    }
                    label={member.is_active ? "Miembro activo" : "Miembro inactivo"}
                  />
                </Stack>
              </Section>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Section title="Perfil deportivo" classname={classes.section}>
                {memberId ? (
                  <OfficialCategoryAssignmentPanel
                    compact
                    disabled={assignableCategories.length === 0}
                    organization={organization}
                    memberId={memberId}
                    currentAssignments={member.current_official_category_assignments || []}
                    onChanged={onSuccess}
                  />
                ) : categoriesLoading ? (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Cargando categorías disponibles…
                    </Typography>
                  </Stack>
                ) : assignableCategories.length ? (
                  <Stack spacing={1.5}>
                    <TextField
                      select
                      fullWidth
                      label="Categoría oficial inicial"
                      value={initialCategoryId}
                      onChange={(event) => setInitialCategoryId(event.target.value)}
                      helperText="Es opcional. Si la elegís, la asignación quedará registrada junto con el alta."
                    >
                      <MenuItem value="">Sin categoría por ahora</MenuItem>
                      {assignableCategories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.modality === "male" ? "Masculino" : "Femenino"} · {category.ladder.name} ·{" "}
                          {category.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    {selectedCategory && (
                      <Alert severity="info">
                        Respaldada por {selectedCategory.classification.name}, temporada{" "}
                        {selectedCategory.classification.current_season.year}.
                      </Alert>
                    )}
                  </Stack>
                ) : (
                  <Alert severity="warning">
                    No hay categorías asignables. Primero debe existir un ranking no archivado con temporada vigente.
                  </Alert>
                )}
              </Section>
            </Grid2>
          </Grid2>

          {memberId && (
            <Section
              title={linkedUser ? "Usuario vinculado" : "Usuario Reva"}
              description={
                linkedUser
                  ? "Cuenta de Reva asociada a este registro de miembro."
                  : "Buscá una cuenta móvil por teléfono o documento para vincularla a este miembro."
              }
              classname={classes.section}
            >
              <MemberUserLinkPanel organizationId={organization.id} member={member} onChanged={handleUserLinkChanged} />
            </Section>
          )}
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <Button onClick={onClose} variant="outlined" disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving || !member.name.trim()}>
            {saving ? "Guardando…" : memberId ? "Guardar cambios" : "Crear miembro"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

export default withStyles(styles)(EditMemberPage);
